import { useEffect, useState } from "react";
import { useReducedMotion } from "framer-motion";

export const EASE = [0.22, 1, 0.36, 1];

export function useFade() {
  const reduce = useReducedMotion();
  return {
    reduce,
    fade: {
      hidden: reduce ? { opacity: 1, y: 0 } : { opacity: 0, y: 22 },
      show: {
        opacity: 1,
        y: 0,
        transition: reduce ? { duration: 0 } : { duration: 0.55, ease: EASE },
      },
    },
    stagger: {
      hidden: {},
      show: { transition: { staggerChildren: reduce ? 0 : 0.08 } },
    },
  };
}

export function CornerMarks({ dark = false }) {
  return (
    <div className="pointer-events-none absolute inset-0" aria-hidden="true">
      <span className="absolute -left-2.5 -top-2.5 h-14 w-14 border-l-[3px] border-t-[3px] border-gold" />
      <span
        className={`absolute -right-2 -top-2 h-10 w-10 border-r border-t ${dark ? "border-white/20" : "border-navy/20"}`}
      />
      <span
        className={`absolute -bottom-2 -left-2 h-10 w-10 border-b border-l ${dark ? "border-white/20" : "border-navy/20"}`}
      />
      <span className="absolute -bottom-2.5 -right-2.5 h-14 w-14 border-b-[3px] border-r-[3px] border-gold" />
    </div>
  );
}

export function ArrowIcon({ className = "h-4 w-4" }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M4 12L12 4M12 4H5.5M12 4V10.5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function MediaSlot({
  src,
  fallback,
  alt,
  label,
  spec,
  className = "",
  fit = "cover",
  position = "object-center",
  tone = "dark",
}) {
  const [source, setSource] = useState(src || fallback || "");
  const [failed, setFailed] = useState(!(src || fallback));

  useEffect(() => {
    setSource(src || fallback || "");
    setFailed(!(src || fallback));
  }, [src, fallback]);
  const showFallback = failed || !source;
  const dark = tone === "dark";
  const objectFit = fit === "contain" ? "object-contain" : "object-cover";

  const onError = () => {
    if (src && fallback && source === src) {
      setSource(fallback);
      return;
    }
    setFailed(true);
  };

  if (showFallback) {
    return (
      <div
        className={`relative overflow-hidden ${dark ? "bg-navy" : "bg-[#f4f3f9]"} ${className}`}
        role="img"
        aria-label={`${alt}. ${spec}`}
      >
        <div
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage: dark
              ? "linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)"
              : "linear-gradient(rgba(16,14,52,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(16,14,52,0.06) 1px, transparent 1px)",
            backgroundSize: "32px 32px",
          }}
        />
        <div className={`absolute -right-10 top-8 h-40 w-40 rounded-full blur-3xl ${dark ? "bg-royal/35" : "bg-royal/15"}`} />
        <div className={`absolute -bottom-12 left-6 h-32 w-32 rounded-full blur-3xl ${dark ? "bg-gold/20" : "bg-gold/25"}`} />
        <div className="relative flex h-full min-h-[inherit] flex-col justify-between gap-6 p-5 sm:p-6">
          <div>
            <p
              className={`text-[11px] font-semibold uppercase tracking-[0.28em] ${
                dark ? "text-gold/90" : "text-navy/40"
              }`}
            >
              {label} · illustration slot
            </p>
            <p className={`mt-3 max-w-sm text-sm font-semibold tracking-tight ${dark ? "text-white" : "text-navy"}`}>
              Commission and drop the asset here
            </p>
          </div>
          <p className={`max-w-sm text-xs leading-relaxed ${dark ? "text-white/55" : "text-navy/50"}`}>{spec}</p>
        </div>
      </div>
    );
  }

  return (
    <img
      src={source}
      alt={alt}
      className={`${objectFit} ${position} ${className}`}
      onError={onError}
    />
  );
}

export function useKnockoutLogo(src) {
  const [href, setHref] = useState("");
  const [failed, setFailed] = useState(!src);

  useEffect(() => {
    if (!src) {
      setFailed(true);
      setHref("");
      return undefined;
    }

    setFailed(false);
    setHref("");

    let cancelled = false;
    const image = new Image();
    image.decoding = "async";

    image.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        const scale = Math.min(1, 1400 / Math.max(image.naturalWidth, image.naturalHeight));
        canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
        canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
        const ctx = canvas.getContext("2d", { willReadFrequently: true });
        if (!ctx) {
          if (!cancelled) setHref(src);
          return;
        }

        ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
        const frame = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const pixels = frame.data;

        for (let i = 0; i < pixels.length; i += 4) {
          const r = pixels[i];
          const g = pixels[i + 1];
          const b = pixels[i + 2];
          const max = Math.max(r, g, b);
          const chroma = max - Math.min(r, g, b);

          if (max < 28 && chroma < 16) {
            pixels[i + 3] = 0;
          } else if (max < 46 && chroma < 12) {
            pixels[i + 3] = Math.round(((max - 28) / 18) * pixels[i + 3]);
          }
        }

        ctx.putImageData(frame, 0, 0);
        if (!cancelled) {
          setFailed(false);
          setHref(canvas.toDataURL("image/png"));
        }
      } catch {
        if (!cancelled) setHref(src);
      }
    };

    image.onerror = () => {
      if (!cancelled) setFailed(true);
    };

    image.src = src;

    return () => {
      cancelled = true;
    };
  }, [src]);

  return { href, failed };
}

